<template>
  <div class="upcoming-sessions-report">
    <b-table striped :fields="sessionFields" :items="ongoingSessions" head-variant="dark">
      <template #cell(counselor-status)="row">
        <div v-if="row.item.attendances.some(x => x.user.id === row.item.counselor.userId)"
             v-b-tooltip.hover.top="'Counselor has attended'"
             class="attended-bar"
        />
        <div v-else v-b-tooltip.hover.top="'Counselor no show'" class="no-show-bar" />
      </template>
      <template #cell(counselor)="row">
        <a :href="`/counselor/profile/${row.item.counselor.id}`">
          {{ row.item.counselor.firstName }} {{ row.item.counselor.lastName }}
        </a>
      </template>
      <template #cell(client-status)="row">
        <div v-if="row.item.attendances.some(x => x.user.id === row.item.client.userId)"
             v-b-tooltip.hover.top="'Client has attended'"
             class="attended-bar"
        />
        <div v-else v-b-tooltip.hover.top="'Client no show'" class="no-show-bar" />
      </template>
      <template #cell(client)="row">
        <a :href="`/student/${row.item.client.userId}`">
          {{ row.item.client.firstName }} {{ row.item.client.lastName }}
        </a>
      </template>
      <template #cell(campus)="row">
        {{ row.item.campus.name }}
      </template>
      <template #cell(start)="row">
        {{ row.item.start | withTimeZone | format('M/D/YYYY h:mmA (z)') }}
      </template>
      <template #cell(payment)="row">
        {{ row.item.payment.status }}
        <template v-if="row.item.payment.status === 'Paid'">
          <template v-if="row.item.payment.type === 'CampusCredit'">
            (Credit)
          </template>
        </template>
      </template>
      <template #cell(show_details)="row">
        <div v-if="!row.item._showDetails">
          <button
            v-if="row.item.payment.status === 'Paid'"
            class="btn btn-ghost"
            type="button"
            @click="toggleRefundPaymentConfirm(row)"
          >
            Refund Payment
          </button>
          <button
            v-else-if="row.item.payment.status === 'Pending'"
            class="btn btn-ghost"
            type="button"
            @click="toggleForgivePaymentConfirm(row)"
          >
            Refund Payment
          </button>
          <button
            v-if="!row.item.canceled"
            class="btn btn-ghost"
            type="button"
            @click="toggleCancelSessionConfirm(row)"
          >
            Cancel Session
          </button>
          <button class="btn btn-ghost" type="button" @click="toggleMoreDetails(row)">
            More
          </button>
        </div>
      </template>
      <template #row-details="row">
        <Loader v-if="acting" style="width: 100%;" />
        <div v-else-if="row.item.mode === 'details'">
          <div>
            <table>
              <th>Attendance</th>
              <th></th>
              <th>
                <button class="btn btn-ghost" type="button" @click="refreshAttendances(row)">
                  Refresh
                </button>
              </th>
              <tr v-if="!row.item.attendances || !row.item.attendances.length">
                No attendance data found.
              </tr>
              <tr v-for="(attendance, index) in row.item.attendances" :key="index">
                <td>{{ attendance.user.firstName }} {{ attendance.user.lastName }}</td>
                <td>{{ attendance.event }}</td>
                <td>{{ attendance.when | withTimeZone | format('M/D/YYYY h:mmA (z)') }}</td>
              </tr>
            </table>
          </div>
          <button class="btn btn-ghost" type="button" @click="row.toggleDetails()">
            Back
          </button>
        </div>
        <div v-else-if="row.item.mode === 'confirm'" class="row-details-container">
          <div class="message">
            {{ row.item.message }}
          </div>
          <div class="buttons">
            <button class="btn btn-warn" type="button" @click="row.item.action(row.item)">
              Confirm
            </button>
            <button class="btn btn-ghost" type="button" @click="row.toggleDetails()">
              Back
            </button>
          </div>
        </div>
      </template>
    </b-table>
    <Loader v-if="fetching" style="width: 100%;" />
    <div v-else-if="!ongoingSessions.length" class="text-center">
      No ongoing sessions found.
    </div>
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue';
import SessionClient from '@/api/SessionClient';
import MeetingsClient from '@/api/MeetingsClient';
import downloadCsv from '@/helpers/mixins/download-csv.js'
import moment from 'moment';

export default {
    name: 'OngoingSessionsReport',
    components:{
        Loader,
    },
    mixins:[downloadCsv],
    data() {
        return {
            sessionsClient: new SessionClient(this.$http),
            meetingsClient: new MeetingsClient(this.$http),
            sessionFields: [
                { key: 'counselor-status', label: '' },
                { key: 'counselor', label: 'Counselor', sortable: true },
                { key: 'client-status', label: '' },
                { key: 'client', label: 'Client', sortable: true },
                { key: 'campus', label: 'Campus', sortable: true },
                { key: 'start', label: 'Start', sortable: true },
                { key: 'payment', label: 'Payment', sortable: true },
                { key: 'show_details', label: '' }
            ],
            refreshInterval: null,

            acting: false,
            fetching: false,
            pageToken: 'ListSessionsByStartTimeAscending',
            ongoingSessions: [],
        }
    },
    async mounted() {
        await this.reset();

        this.refreshInterval = setInterval(() => this.reset(), 360000)
    },
    beforeDestroy() {
      clearInterval(this.refreshInterval);
    },
    methods: {
        reset() {
            this.pageToken = 'ListSessionsByStartTimeAscending';
            this.ongoingSessions.length = 0;

            return this.fetchAllRecords();
        },
        async fetchAllRecords() {
            try {
                this.fetching = true;

                do {
                    const response = await this.fetchSessions(this.pageToken, 50);
                    this.pageToken = response.nextPageToken;

                    if (!response.sessions || !response.sessions.length) { break; }

                    const meetingIds = response.sessions.map(x => x.meetingId);
                    const attendances = await this.fetchAttendances(meetingIds);

                    response.sessions.forEach(outcome => {
                        this.ongoingSessions.push({
                            ...outcome,
                            attendances: attendances.filter(x => x.meetingId === outcome.meetingId),
                            start: moment(outcome.start),
                            end: moment(outcome.end),
                        });
                    });
                } while (this.pageToken)
            } catch (ex) {
                console.error(ex);
            } finally {
                this.fetching = false;
            }
        },
        async fetchAttendances(meetingIds) {
          const response = await this.meetingsClient.getAttendances(meetingIds);

          return response.attendances.map(x => ({
              ...x,
              when: moment(x.when),
          }));
        },
        async fetchSessions(pageToken, recordCount) {
            const canceled = false;
            const fromDate = moment().startOf('minute').subtract(60, 'minutes').toISOString()
            const throughDate = moment().startOf('minute').add(16, 'minutes').toISOString();

            return await this.sessionsClient.getSessionsForAdmin(
                fromDate,
                throughDate,
                undefined,
                canceled,
                recordCount,
                pageToken);
        },
        async toggleMoreDetails(row) {
            row.item.mode = 'details';
            row.toggleDetails();
        },
        async refreshAttendances(row) {
          try {
            this.acting = true;
            row.item.attendances = await this.fetchAttendances([row.item.meetingId]);
          } catch (ex) {
            console.error(ex);
          } finally {
            this.acting = false;
          }
        },
        toggleRefundPaymentConfirm(row) {
            row.item.mode = 'confirm';
            row.item.action = this.refundPayment.bind(this);
            row.item.message = 'Are you sure you want to refund this session\'s payment?';
            row.toggleDetails();
        },
        toggleForgivePaymentConfirm(row) {
            row.item.mode = 'confirm';
            row.item.action = this.refundPayment.bind(this);
            row.item.message = 'Are you sure you want to cancel this session\'s payment?';
            row.toggleDetails();
        },
        async refundPayment(session) {
            try {
                this.acting = true;
                const res = await this.sessionsClient.admin_refund_session(session.id)
                this.$store.commit('setGlobalAlert', {
                    msg: 'Payment Forgiven.',
                });
            } catch (ex) {
                console.error(ex);
                this.$store.commit('setGlobalAlert', {
                    msg: 'Failed to forgive session. Please try again!', error: true,
                });
            } finally {
                this.acting = false;
            }
            this.reset(this.page);
        },
        toggleCancelSessionConfirm(row) {
            row.item.mode = 'confirm';
            row.item.action = this.cancelSession.bind(this);
            row.item.message = 'Are you sure you want to cancel this session?';
            row.toggleDetails();
        },
        async cancelSession(session) {
            try {
                this.acting = true;
                const res = await this.sessionsClient.admin_cancel_session(session.id)
                this.$store.commit('setGlobalAlert', {
                    msg: 'Session Canceled.',
                });
            } catch (ex) {
                console.error(ex);
                this.$store.commit('setGlobalAlert', {
                    msg: 'Failed to cancel session. Please try again!', error: true,
                });
            } finally {
                this.acting = false;
            }
            this.reset(this.page);
        },
    }
}
</script>

<style lang="scss">
.upcoming-sessions-report {
    .form__input {
        margin-right: 10px;
    }
    div.btn-export {
        align-self: center;
    }
    div.no-show-bar {
      display: inline-block;
      border-radius: 5px;
      width: 5px;
      height: 25px;
      background-color: $red;
    }
    div.attended-bar {
      display: inline-block;
      border-radius: 5px;
      width: 5px;
      height: 25px;
      background-color: $approval-green;
    }
    .b-table {
        color: $white;
        .thead-dark th{
            background-color: $dark-blue !important;
            border-color: $border-color !important;
        }
        .canceled-bar {
            border-radius: 5px;
            width: 5px;
            height: 25px;
            background-color: $red;
        }
        .row-details-container {
            margin-bottom: 25px;

            div.message {
                margin-top: 25px;
                margin-bottom: 25px;
            }
        }
        button.btn {
            margin-right: 5px;
        }
        button.btn-warn {
            color: $white;
            background-color: $red;
        }
    }
}
</style>
